import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { organizationService } from '../services/organization';
import { employeeService } from '../services/employee';
import {
  Organization,
  OrganizationRestaurant,
  Employee,
  CreateEmployeeRequest,
  EmployeeRole,
  AppRole,
} from '../types';
import { useForm } from 'react-hook-form';
import { ConfirmationModal } from '../components/ConfirmationModal';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  UtensilsCrossed,
  Shield,
  KeyRound,
  Mail,
  Phone,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { PageSkeleton, EmptyState, ButtonLoader } from '../components/LoadingStates';

interface EmployeeFormData {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  mobile?: string;
  password?: string;
}

type RestaurantRoleState = {
  restaurantId: string;
  restaurantName: string;
  assigned: boolean;
  roles: EmployeeRole[];
};

export const EmployeeManagement: React.FC = () => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();

  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [managingEmployee, setManagingEmployee] = useState<Employee | null>(null);
  const [restaurantRoleStates, setRestaurantRoleStates] = useState<RestaurantRoleState[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'warning' | 'danger' | 'info';
    confirmText: string;
  }>({
    isOpen: false, title: '', message: '', onConfirm: () => {}, type: 'warning', confirmText: 'Confirm',
  });

  const form = useForm<EmployeeFormData>();

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: organizations = [], isLoading: orgsLoading } = useQuery(
    ['organizations-emp'],
    organizationService.getOrganizations
  );

  const selectedOrgCode = organizations.find(o => o.id === selectedOrgId)?.code;

  const { data: roles = [] } = useQuery(
    ['org-roles-emp', selectedOrgCode],
    () => organizationService.getRolesByOrgCode(selectedOrgCode!),
    { enabled: !!selectedOrgCode }
  );

  const getRoleColor = (key: string) => roles.find(r => r.key === key)?.color || 'bg-gray-100 text-gray-700';
  const getRoleLabel = (key: string) => roles.find(r => r.key === key)?.label || key.replace('_', ' ');

  const { data: employees = [], isLoading: empsLoading } = useQuery(
    ['employees', selectedOrgId],
    () => employeeService.getEmployeesByOrganization(selectedOrgId),
    { enabled: !!selectedOrgId }
  );

  const { data: restaurants = [] } = useQuery(
    ['org-restaurants-emp', selectedOrgId],
    () => organizationService.getRestaurantsByOrganization(selectedOrgId),
    { enabled: !!selectedOrgId }
  );

  // Auto-select first org
  useEffect(() => {
    if (organizations.length > 0 && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation(
    (data: CreateEmployeeRequest) => employeeService.createEmployee(selectedOrgId, data),
    {
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to create employee'); },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateEmployeeRequest> }) =>
      employeeService.updateEmployee(selectedOrgId, id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employees', selectedOrgId]);
        closeEmployeeModal();
        toast.success('Employee updated successfully');
      },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to update employee'); },
    }
  );

  const deleteMutation = useMutation(
    (id: string) => employeeService.deleteEmployee(selectedOrgId, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employees', selectedOrgId]);
        toast.success('Employee deleted');
      },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to delete employee'); },
    }
  );

  const toggleStatusMutation = useMutation(
    (id: string) => employeeService.toggleEmployeeStatus(selectedOrgId, id),
    {
      onSuccess: () => { queryClient.invalidateQueries(['employees', selectedOrgId]); },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to update status'); },
    }
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggleExpand = (empId: string) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      next.has(empId) ? next.delete(empId) : next.add(empId);
      return next;
    });
  };

  const openCreateEmployee = () => {
    setEditingEmployee(null);
    form.reset();
    const states: RestaurantRoleState[] = restaurants.map(r => ({
      restaurantId: r.id,
      restaurantName: r.name,
      assigned: false,
      roles: [],
    }));
    setRestaurantRoleStates(states);
    setShowEmployeeModal(true);
  };

  const openEditEmployee = (emp: Employee) => {
    setEditingEmployee(emp);
    form.reset({
      firstName: emp.firstName ?? '',
      middleName: emp.middleName ?? '',
      lastName: emp.lastName ?? '',
      email: emp.email,
      mobile: emp.mobile,
      password: '',
    });
    setShowEmployeeModal(true);
  };

  const closeEmployeeModal = () => {
    setShowEmployeeModal(false);
    setEditingEmployee(null);
    form.reset();
  };

  const onEmployeeSubmit = async (data: EmployeeFormData) => {
    if (editingEmployee) {
      const payload: Partial<CreateEmployeeRequest> = {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        mobile: data.mobile,
      };
      if (data.password) payload.password = data.password;
      updateMutation.mutate({ id: editingEmployee.id, data: payload });
    } else {
      try {
        const created = await createMutation.mutateAsync({ ...data, password: data.password!, organizationId: selectedOrgId });
        for (const state of restaurantRoleStates) {
          if (state.assigned && state.roles.length > 0) {
            await employeeService.assignRestaurantRole(selectedOrgId, created.id, state.restaurantId, state.roles);
          }
        }
        queryClient.invalidateQueries(['employees', selectedOrgId]);
        closeEmployeeModal();
        toast.success('Employee created successfully');
      } catch {
        // error handled by mutation onError
      }
    }
  };

  const openManageRoles = (emp: Employee) => {
    setManagingEmployee(emp);
    const states: RestaurantRoleState[] = restaurants.map(r => {
      const existing = emp.restaurantRoles?.find(rr => rr.restaurantId === r.id);
      return {
        restaurantId: r.id,
        restaurantName: r.name,
        assigned: !!existing,
        roles: existing ? (existing.roles as EmployeeRole[]) : [],
      };
    });
    setRestaurantRoleStates(states);
    setShowRolesModal(true);
  };

  const toggleRestaurantAssign = (restaurantId: string) => {
    setRestaurantRoleStates(prev =>
      prev.map(s =>
        s.restaurantId === restaurantId
          ? { ...s, assigned: !s.assigned, roles: s.assigned ? [] : s.roles }
          : s
      )
    );
  };

  const toggleRole = (restaurantId: string, role: EmployeeRole) => {
    setRestaurantRoleStates(prev =>
      prev.map(s => {
        if (s.restaurantId !== restaurantId) return s;
        const roles = s.roles.includes(role)
          ? s.roles.filter(r => r !== role)
          : [...s.roles, role];
        return { ...s, roles };
      })
    );
  };

  const saveRoles = async () => {
    if (!managingEmployee) return;
    setSavingRoles(true);
    try {
      for (const state of restaurantRoleStates) {
        if (state.assigned && state.roles.length > 0) {
          await employeeService.assignRestaurantRole(selectedOrgId, managingEmployee.id, state.restaurantId, state.roles);
        } else if (!state.assigned) {
          const wasAssigned = managingEmployee.restaurantRoles?.some(rr => rr.restaurantId === state.restaurantId);
          if (wasAssigned) {
            await employeeService.removeRestaurantRole(selectedOrgId, managingEmployee.id, state.restaurantId);
          }
        }
      }
      queryClient.invalidateQueries(['employees', selectedOrgId]);
      toast.success('Restaurant roles saved');
      setShowRolesModal(false);
      setManagingEmployee(null);
    } catch {
      toast.error('Failed to save some role assignments');
    } finally {
      setSavingRoles(false);
    }
  };

  const confirmDelete = (emp: Employee) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Employee',
      message: `Are you sure you want to delete "${getEmpDisplayName(emp)}"? All their restaurant role assignments will also be removed.`,
      onConfirm: () => {
        deleteMutation.mutate(emp.id);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      },
      type: 'danger',
      confirmText: 'Delete',
    });
  };

  const getRestaurantName = (restaurantId: string): string => {
    return restaurants.find(r => r.id === restaurantId)?.name || restaurantId;
  };

  const getEmpDisplayName = (emp: Employee): string => {
    const full = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(' ');
    return full || emp.name || '—';
  };

  const filteredEmployees = employees.filter(emp =>
    getEmpDisplayName(emp).toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOrg = organizations.find(o => o.id === selectedOrgId);
  const isModalLoading = createMutation.isLoading || updateMutation.isLoading;

  if (orgsLoading) return <PageSkeleton type="grid" />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <Users className="h-6 w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Employee Management
          </h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>
            Manage employees and their restaurant role assignments
          </p>
        </div>
        {selectedOrgId && (
          <button
            onClick={openCreateEmployee}
            className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
            style={{ backgroundColor: currentTheme.colors.primary[600] }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Employee
          </button>
        )}
      </div>

      {/* Organization Selector */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-sm">
          <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
            Select Organization
          </label>
          <select
            value={selectedOrgId}
            onChange={e => { setSelectedOrgId(e.target.value); setExpandedEmployees(new Set()); }}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
            style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
          >
            <option value="">-- Select Organization --</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name} ({org.code})</option>
            ))}
          </select>
        </div>

        {selectedOrgId && (
          <div className="flex-1 max-w-sm">
            <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
              Search Employees
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                style={{ borderColor: currentTheme.colors.border.primary }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Stats bar */}
      {selectedOrg && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-lg mb-5 border"
          style={{ backgroundColor: currentTheme.colors.primary[50], borderColor: currentTheme.colors.primary[200] }}
        >
          <Building2 className="h-5 w-5 flex-shrink-0" style={{ color: currentTheme.colors.primary[600] }} />
          <span className="font-semibold" style={{ color: currentTheme.colors.primary[700] }}>{selectedOrg.name}</span>
          <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
            · {employees.length} employee{employees.length !== 1 ? 's' : ''}
            · {restaurants.length} restaurant{restaurants.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Content */}
      {!selectedOrgId ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Building2 className="h-12 w-12 mb-3" style={{ color: currentTheme.colors.text.secondary }} />
          <p className="text-lg font-medium" style={{ color: currentTheme.colors.text.secondary }}>
            Select an organization to manage employees
          </p>
        </div>
      ) : empsLoading ? (
        <PageSkeleton type="grid" />
      ) : filteredEmployees.length === 0 ? (
        <EmptyState
          icon={<Users className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Employees Found"
          description={searchTerm ? 'Try adjusting your search.' : 'Get started by adding your first employee.'}
          action={!searchTerm ? (
            <button
              onClick={openCreateEmployee}
              className="inline-flex items-center px-4 py-2 text-white rounded-lg"
              style={{ backgroundColor: currentTheme.colors.primary[600] }}
            >
              <Plus className="h-5 w-5 mr-2" /> Add First Employee
            </button>
          ) : undefined}
        />
      ) : (
        <div className="space-y-3">
          {filteredEmployees.map(emp => {
            const isExpanded = expandedEmployees.has(emp.id);
            const empRoles = emp.restaurantRoles || [];

            return (
              <div
                key={emp.id}
                className="rounded-xl border shadow-sm overflow-hidden"
                style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: '#fff' }}
              >
                {/* Employee Row */}
                <div className="flex items-center justify-between px-5 py-4"
                  style={{ backgroundColor: currentTheme.colors.background.secondary || '#fafafa' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      onClick={() => toggleExpand(emp.id)}
                      className="flex-shrink-0 p-1 rounded"
                      style={{ color: currentTheme.colors.primary[600] }}
                    >
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </button>

                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0"
                      style={{ backgroundColor: currentTheme.colors.primary[100], color: currentTheme.colors.primary[700] }}>
                      {(emp.firstName || emp.name || '?').charAt(0).toUpperCase()}
                    </div>

                    <div className="min-w-0">
                      <p className="font-semibold truncate" style={{ color: currentTheme.colors.text.primary }}>
                        {getEmpDisplayName(emp)}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                          <Mail className="h-3 w-3" /> {emp.email}
                        </span>
                        {emp.mobile && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                            <Phone className="h-3 w-3" /> {emp.mobile}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className={clsx('px-2.5 py-0.5 rounded-full text-xs font-medium',
                      emp.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>
                      {emp.active ? 'Active' : 'Inactive'}
                    </span>

                    {isExpanded && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {empRoles.length} restaurant{empRoles.length !== 1 ? 's' : ''}
                      </span>
                    )}


                    <button
                      onClick={() => openManageRoles(emp)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[700],
                        border: `1px solid ${currentTheme.colors.primary[200]}` }}
                      title="Manage Restaurant Roles"
                    >
                      <Shield className="h-3.5 w-3.5" /> Roles
                    </button>

                    <button onClick={() => openEditEmployee(emp)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: currentTheme.colors.primary[600], backgroundColor: currentTheme.colors.primary[100] }}
                      title="Edit Employee">
                      <Edit className="h-4 w-4" />
                    </button>

                    <button onClick={() => toggleStatusMutation.mutate(emp.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: emp.active ? '#fef2f2' : '#f0fdf4',
                        color: emp.active ? '#dc2626' : '#16a34a',
                        border: `1px solid ${emp.active ? '#fecaca' : '#bbf7d0'}`
                      }}
                      title={emp.active ? 'Deactivate' : 'Activate'}>
                      {emp.active ? 'Deactivate' : 'Activate'}
                    </button>

                    <button onClick={() => confirmDelete(emp)}
                      className="p-2 rounded-lg bg-red-50 text-red-600" title="Delete Employee">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded: Restaurant Role Tree */}
                {isExpanded && (
                  <div className="px-5 py-3 bg-white">
                    {empRoles.length === 0 ? (
                      <div className="flex items-center gap-3 py-3 pl-8">
                        <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                          No restaurant roles assigned.
                        </span>
                        <button onClick={() => openManageRoles(emp)}
                          className="inline-flex items-center gap-1 text-sm font-medium"
                          style={{ color: currentTheme.colors.primary[600] }}>
                          <Shield className="h-4 w-4" /> Assign Roles
                        </button>
                      </div>
                    ) : (
                      empRoles.map((rr, index) => {
                        const isLast = index === empRoles.length - 1;
                        return (
                          <div key={rr.restaurantId} className="flex items-start">
                            <div className="flex flex-col items-center mr-3 mt-1" style={{ width: 24 }}>
                              <div className="w-px" style={{ height: 12, backgroundColor: currentTheme.colors.border.primary }} />
                              <div className="flex items-center" style={{ height: 20 }}>
                                <div className="w-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }} />
                              </div>
                              {!isLast && (
                                <div className="w-px flex-1" style={{ minHeight: 12, backgroundColor: currentTheme.colors.border.primary }} />
                              )}
                            </div>

                            <div className="flex-1 flex items-center flex-wrap gap-2 px-4 py-3 mb-1 rounded-lg border"
                              style={{ borderColor: currentTheme.colors.border.primary }}>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: '#f0fdf4' }}>
                                  <UtensilsCrossed className="h-4 w-4 text-green-600" />
                                </div>
                                <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                                  {getRestaurantName(rr.restaurantId)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 flex-wrap ml-2">
                                {rr.roles.map(role => (
                                  <span key={role} className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                                    getRoleColor(role))}>
                                    {getRoleLabel(role)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Employee Modal */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h2>
              <button onClick={closeEmployeeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {selectedOrg && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm"
                style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[700] }}>
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">{selectedOrg.name}</span>
              </div>
            )}

            <form onSubmit={form.handleSubmit(onEmployeeSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    {...form.register('firstName', { required: 'First name is required' })}
                    type="text" placeholder="e.g., RAHUL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  />
                  {form.formState.errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    {...form.register('lastName', { required: 'Last name is required' })}
                    type="text" placeholder="e.g., NAYAK"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  />
                  {form.formState.errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                <input
                  {...form.register('middleName')}
                  type="text" placeholder="e.g., KUMAR (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Login Username) {!editingEmployee && '*'}
                </label>
                <input
                  {...form.register('email', { required: !editingEmployee ? 'Email is required' : false })}
                  type="email" placeholder="employee@example.com"
                  disabled={!!editingEmployee}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                />
                {form.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    {...form.register('mobile')}
                    type="text" placeholder="9876543210"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <KeyRound className="h-3.5 w-3.5" />
                    {editingEmployee ? 'New Password' : 'Password *'}
                  </label>
                  <input
                    {...form.register('password', { required: !editingEmployee ? 'Password is required' : false })}
                    type="password" placeholder={editingEmployee ? 'Leave blank to keep' : 'Min 6 characters'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  />
                  {form.formState.errors.password && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.password.message}</p>
                  )}
                </div>
              </div>

              {/* Restaurant Role Assignments - only shown on Create */}
              {!editingEmployee && restaurants.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" style={{ color: currentTheme.colors.primary[600] }} />
                    Restaurant Roles
                    <span className="font-normal text-gray-400 ml-1">(optional)</span>
                  </label>
                  <div className="space-y-2 max-h-52 overflow-y-auto border rounded-lg p-2"
                    style={{ borderColor: currentTheme.colors.border.primary }}>
                    {restaurantRoleStates.map(state => (
                      <div key={state.restaurantId}
                        className="border rounded-lg p-3 transition-colors"
                        style={{
                          borderColor: state.assigned ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                          backgroundColor: state.assigned ? currentTheme.colors.primary[50] : 'white',
                        }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <UtensilsCrossed className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                              {state.restaurantName}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleRestaurantAssign(state.restaurantId)}
                            className="text-xs px-2.5 py-1 rounded-md font-medium transition-colors"
                            style={state.assigned
                              ? { backgroundColor: currentTheme.colors.primary[600], color: 'white' }
                              : { backgroundColor: '#f3f4f6', color: '#6b7280' }}
                          >
                            {state.assigned ? 'Assigned' : 'Assign'}
                          </button>
                        </div>
                        {state.assigned && (
                          roles.length === 0 ? (
                            <p className="mt-2 text-xs text-amber-600">No roles configured. Add roles in Organization → Roles tab first.</p>
                          ) : (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {roles.map(role => (
                                <button
                                  key={role.key}
                                  type="button"
                                  onClick={() => toggleRole(state.restaurantId, role.key as EmployeeRole)}
                                  className={clsx(
                                    'px-2 py-0.5 rounded-full text-xs font-medium border transition-all',
                                    state.roles.includes(role.key as EmployeeRole)
                                      ? clsx(role.color, 'border-transparent')
                                      : 'bg-gray-50 text-gray-400 border-gray-200'
                                  )}
                                >
                                  {role.label}
                                </button>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeEmployeeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" disabled={isModalLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={e => !isModalLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={e => !isModalLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}>
                  {isModalLoading
                    ? <ButtonLoader text={editingEmployee ? 'Updating...' : 'Creating...'} />
                    : editingEmployee ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Restaurant Roles Modal */}
      {showRolesModal && managingEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                  Restaurant Role Assignments
                </h2>
                <p className="text-sm mt-0.5" style={{ color: currentTheme.colors.text.secondary }}>
                  {getEmpDisplayName(managingEmployee)} — assign roles per restaurant
                </p>
              </div>
              <button onClick={() => { setShowRolesModal(false); setManagingEmployee(null); }}
                className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {restaurants.length === 0 ? (
                <p className="text-center py-8 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                  No restaurants found for this organization.
                </p>
              ) : (
                restaurantRoleStates.map(state => (
                  <div key={state.restaurantId}
                    className="border rounded-lg p-4"
                    style={{ borderColor: state.assigned ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                      backgroundColor: state.assigned ? currentTheme.colors.primary[50] : '#fff' }}>
                    {/* Restaurant header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UtensilsCrossed className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-sm" style={{ color: currentTheme.colors.text.primary }}>
                          {state.restaurantName}
                        </span>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs font-medium" style={{ color: currentTheme.colors.text.secondary }}>
                          {state.assigned ? 'Assigned' : 'Not Assigned'}
                        </span>
                        <div
                          className={clsx('w-10 h-5 rounded-full relative transition-colors cursor-pointer',
                            state.assigned ? 'bg-green-500' : 'bg-gray-300')}
                          onClick={() => toggleRestaurantAssign(state.restaurantId)}
                        >
                          <div className={clsx('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                            state.assigned ? 'translate-x-5' : 'translate-x-0.5')} />
                        </div>
                      </label>
                    </div>

                    {/* Roles checkboxes */}
                    {state.assigned && (
                      roles.length === 0 ? (
                        <p className="text-xs text-amber-600 mt-1">No roles configured. Add roles in Organization → Roles tab first.</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {roles.map(role => (
                            <label key={role.key}
                              className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/60">
                              <input
                                type="checkbox"
                                checked={state.roles.includes(role.key as EmployeeRole)}
                                onChange={() => toggleRole(state.restaurantId, role.key as EmployeeRole)}
                                className="rounded"
                                style={{ accentColor: currentTheme.colors.primary[600] }}
                              />
                              <span className={clsx('text-xs font-medium px-1.5 py-0.5 rounded-full', role.color)}>
                                {role.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )
                    )}

                    {state.assigned && state.roles.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">Select at least one role</p>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 mt-2 border-t">
              <button onClick={() => { setShowRolesModal(false); setManagingEmployee(null); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={saveRoles}
                disabled={savingRoles || restaurantRoleStates.some(s => s.assigned && s.roles.length === 0)}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={e => !savingRoles && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                onMouseLeave={e => !savingRoles && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}>
                {savingRoles ? <ButtonLoader text="Saving..." /> : 'Save Role Assignments'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        type={confirmationModal.type}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};
